// The shared vocabulary of the settings and organization pages (spec/dashboard.md section 12.4; spec/api-conventions.md
// sections 2, 11 and 20.1): the scopes, the roles, a key's status, the audit filters in the URL, error text.
import { ApiError } from '../../api/client';
import type { GameRole, OrgRole, ServiceKey } from '../../api/types';
import type { Tone } from '../../ui/Chip';
import { formatDuration } from '../../ui/time';

/** The seven service-key scopes (section 2 of the API). */
export const SCOPES = ['content:read', 'content:write', 'deploy', 'players:read', 'players:write', 'analytics:read', 'admin'] as const;

export const GAME_ROLES: NonNullable<GameRole>[] = ['admin', 'developer', 'designer', 'qa', 'release', 'support', 'viewer'];
export const ORG_ROLES: OrgRole[] = ['owner', 'admin', 'member'];

/** Owners and admins hold every organization permission; a member holds `org_read` only (RoleMatrix). */
export function orgRoleManages(role: OrgRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export const DEFAULT_GRACE_SECONDS = 3600;

export interface KeyStatus {
  word: string;
  tone: Tone;
}

/** active / revoked / expired / rotating until … (the old key of a rotation works until its grace ends). */
export function keyStatus(k: ServiceKey, when: (v: string | null | undefined) => string, now = Date.now()): KeyStatus {
  if (k.revokedAt) return { word: 'revoked', tone: 'danger' };
  if (k.expiresAt && Date.parse(k.expiresAt) <= now) return { word: 'expired', tone: 'warning' };
  if (k.rotationGraceUntil) {
    return Date.parse(k.rotationGraceUntil) > now ? { word: `rotating until ${when(k.rotationGraceUntil)}`, tone: 'warning' } : { word: 'rotated', tone: 'danger' };
  }
  return { word: 'active', tone: 'success' };
}

/** A key that can still be rotated or revoked. */
export function keyLive(k: ServiceKey, now = Date.now()): boolean {
  if (k.revokedAt) return false;
  if (k.rotationGraceUntil && Date.parse(k.rotationGraceUntil) <= now) return false;
  return true;
}

export function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'internal_error', e instanceof Error ? e.message : 'Something went wrong.', '');
}

/**
 * The audit filters, in the URL under these names and sent as query parameters of the same names (spec/api-conventions.md
 * sections 11 and 21.1): the game log's set; the organization log adds `game`; the caller's own log (`GET /v1/me/audit`)
 * offers `MY_AUDIT_FILTER_KEYS`.
 */
export const AUDIT_FILTER_KEYS = ['action', 'actorType', 'actorId', 'user', 'resourceType', 'resourceId', 'requestId', 'branch', 'environment', 'from', 'to'] as const;
export const MY_AUDIT_FILTER_KEYS = ['action', 'resourceType', 'resourceId', 'requestId', 'from', 'to', 'organization', 'game'] as const;
export type AuditFilterKey = (typeof AUDIT_FILTER_KEYS)[number] | (typeof MY_AUDIT_FILTER_KEYS)[number];
/** The environment kinds the game log's `environment` filter takes by name. */
export const ENVIRONMENT_KINDS = ['dev', 'stage', 'prod'] as const;
export type AuditFilters = Partial<Record<AuditFilterKey, string>>;
export const ACTOR_TYPES = ['user', 'service_key', 'system', 'player'] as const;
export const AUDIT_PAGE_SIZE = 50;

export function readAuditFilters(params: URLSearchParams, keys: readonly AuditFilterKey[]): AuditFilters {
  const out: AuditFilters = {};
  for (const k of keys) {
    const v = params.get(k)?.trim();
    if (v) out[k] = v;
  }
  return out;
}

export function filtersKey(filters: AuditFilters): string {
  return Object.keys(filters)
    .sort()
    .map((k) => `${k}=${filters[k as AuditFilterKey]}`)
    .join('&');
}

/** "3h ago" for a last-used instant (the largest whole unit), "never" when the credential was never used. */
export function lastUsedText(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const seconds = Math.max(0, Math.floor((now - ms) / 1000));
  const unit = seconds >= 86400 ? 86400 : seconds >= 3600 ? 3600 : seconds >= 60 ? 60 : 1;
  const coarse = Math.floor(seconds / unit) * unit;
  return seconds < 60 ? 'just now' : `${formatDuration(coarse)} ago`;
}

export function isoToSeconds(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

export function secondsToIso(seconds: number | null): string | null {
  return seconds === null ? null : new Date(seconds * 1000).toISOString();
}
